import {
  Timestamp,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Payment } from '../types/firestore';
import { functions } from './firebase';
import tenantService, {
  tenantCollection,
  tenantDocument,
} from './tenantService';

export type StudentPaymentMethod =
  | 'cash'
  | 'bank_transfer'
  | 'online'
  | 'card'
  | 'check'
  | 'paytabs'
  | 'xpay'
  | 'other';

export type StudentPaymentStatus =
  | 'pending'
  | 'paid'
  | 'overdue'
  | 'cancelled'
  | 'refunded';

export interface StudentPayment extends Payment {
  studentName?: string;
  tenantId?: string;
  isActive?: boolean;
  subtotalAmount?: number;
  vatAmount?: number;
  vatRate?: number;
  accountingStatus?: 'draft' | 'posted' | 'reversed';
  journalEntryId?: string;
  cashJournalEntryId?: string;
  xpayJournalEntryId?: string;
  reversalJournalEntryIds?: string[];
  reversalReason?: string;
  paymentMethod: StudentPaymentMethod;
  status: StudentPaymentStatus;
}

export interface CreateStudentInvoiceInput {
  studentId: string;
  studentName?: string;
  amount: number;
  paymentMethod: StudentPaymentMethod;
  type?: 'tuition' | 'registration' | 'materials' | 'exam' | 'other';
  description: string;
  dueDate?: string | Date | Timestamp | null;
  notes?: string;
  referenceNumber?: string;
  receipt?: boolean;
}

const allowedPaymentMethods: StudentPaymentMethod[] = [
  'cash',
  'bank_transfer',
  'online',
  'card',
  'check',
  'paytabs',
  'xpay',
  'other',
];

const normalizePaymentMethod = (method?: string): StudentPaymentMethod =>
  allowedPaymentMethods.includes(method as StudentPaymentMethod)
    ? (method as StudentPaymentMethod)
    : 'other';

const isOverdue = (status: string, dueDate?: Timestamp | null) =>
  status === 'pending' &&
  Boolean(dueDate) &&
  dueDate!.toMillis() < Date.now();

const normalizePayment = (id: string, data: any): StudentPayment => {
  const dueDate = data.dueDate instanceof Timestamp ? data.dueDate : null;
  const status = isOverdue(data.status || 'pending', dueDate)
    ? 'overdue'
    : data.status || 'pending';

  return {
    id,
    ...data,
    amount: Number(data.amount || 0),
    currency: 'EGP',
    paymentMethod: normalizePaymentMethod(data.paymentMethod || data.method),
    status,
    dueDate: dueDate || undefined,
  } as StudentPayment;
};

class StudentPaymentsService {
  async listTeacherPayments(teacherId: string): Promise<StudentPayment[]> {
    const snapshot = await getDocs(
      query(
        tenantCollection(teacherId, 'payments'),
        where('teacherId', '==', teacherId),
        orderBy('createdAt', 'desc')
      )
    );

    return snapshot.docs.map(document =>
      normalizePayment(document.id, document.data())
    );
  }

  async listStudentPayments(
    studentId: string,
    teacherIds?: string[]
  ): Promise<StudentPayment[]> {
    const resolvedTeacherIds =
      teacherIds && teacherIds.length > 0
        ? teacherIds
        : (await tenantService.getMemberships(studentId)).map(
            membership => membership.teacherId
          );

    const snapshots = await Promise.all(
      resolvedTeacherIds.map(teacherId =>
        getDocs(
          query(
            tenantCollection(teacherId, 'payments'),
            where('studentId', '==', studentId),
            orderBy('createdAt', 'desc')
          )
        )
      )
    );

    return snapshots.flatMap(snapshot =>
      snapshot.docs.map(document =>
        normalizePayment(document.id, document.data())
      )
    );
  }

  async createStudentInvoice(
    teacherId: string,
    input: CreateStudentInvoiceInput
  ): Promise<string> {
    const callable = httpsCallable<
      {
        teacherId: string;
        studentId: string;
        studentName?: string;
        amount: number;
        paymentMethod: StudentPaymentMethod;
        type?: 'tuition' | 'registration' | 'materials' | 'exam' | 'other';
        description: string;
        dueDate?: string;
        notes?: string;
        referenceNumber?: string;
        receipt?: boolean;
      },
      { paymentId: string }
    >(functions, 'createStudentInvoiceWithAccounting');
    const result = await callable({
      teacherId,
      studentId: input.studentId,
      studentName: input.studentName,
      amount: Number(input.amount),
      paymentMethod: normalizePaymentMethod(input.paymentMethod),
      type: input.type || 'tuition',
      description: input.description,
      dueDate:
        typeof input.dueDate === 'string'
          ? input.dueDate
          : input.dueDate instanceof Date
            ? input.dueDate.toISOString().slice(0, 10)
            : undefined,
      notes: input.notes,
      referenceNumber: input.referenceNumber,
      receipt: input.receipt,
    });
    return result.data.paymentId;
  }

  async markStudentPaymentPaid(
    teacherId: string,
    paymentId: string
  ): Promise<void> {
    const callable = httpsCallable<
      { teacherId: string; paymentId: string },
      { success: boolean }
    >(functions, 'markStudentPaymentPaidWithAccounting');
    await callable({ teacherId, paymentId });
  }

  async cancelStudentPayment(
    teacherId: string,
    paymentId: string,
    reason?: string
  ): Promise<void> {
    const callable = httpsCallable<
      { teacherId: string; paymentId: string; action: 'cancel'; reason?: string },
      { success: boolean }
    >(functions, 'reverseStudentPaymentWithAccounting');
    await callable({ teacherId, paymentId, action: 'cancel', reason });
  }

  async refundStudentPayment(
    teacherId: string,
    paymentId: string,
    reason?: string
  ): Promise<void> {
    const callable = httpsCallable<
      { teacherId: string; paymentId: string; action: 'refund'; reason?: string },
      { success: boolean }
    >(functions, 'reverseStudentPaymentWithAccounting');
    await callable({ teacherId, paymentId, action: 'refund', reason });
  }

  async refundXpayStudentPayment(
    teacherId: string,
    paymentId: string,
    reason?: string
  ): Promise<void> {
    const callable = httpsCallable<
      { teacherId: string; paymentId: string; reason?: string },
      { success: boolean; refundId?: string; status?: string }
    >(functions, 'refundXpayStudentPayment');
    await callable({ teacherId, paymentId, reason });
  }

  async updateStudentPaymentStatus(
    teacherId: string,
    paymentId: string,
    status: StudentPaymentStatus
  ): Promise<void> {
    const paymentRef = tenantDocument(teacherId, 'payments', paymentId);
    const paymentSnapshot = await getDoc(paymentRef);
    if (!paymentSnapshot.exists()) {
      throw new Error('Payment does not belong to the current teacher');
    }

    await updateDoc(paymentRef, {
      status,
      ...(status === 'paid' ? { paidAt: serverTimestamp() } : {}),
      updatedAt: serverTimestamp(),
    });
  }
}

export const studentPaymentsService = new StudentPaymentsService();
export default studentPaymentsService;
